import { useState, useEffect } from 'react';
import { useCMS } from '../../context/CMSContext';
import { Save, Loader2, Sparkles, MessageSquare, Tag, RotateCcw, Clock } from 'lucide-react';

export default function AdminCMS() {
  const { content, loading, updateContent } = useCMS();
  const [formData, setFormData] = useState(content);
  const [isSaving, setIsSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState(null); // 'success', 'error', null

  // Update local form state when CMS context loads/changes
  useEffect(() => {
    if (!loading) {
      setFormData(content);
    }
  }, [content, loading]);

  const handleChange = (e) => {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setIsSaving(true);
    setSaveStatus(null);
    try {
      await updateContent(formData);
      setSaveStatus('success');
      setTimeout(() => setSaveStatus(null), 3000);
    } catch (error) {
      setSaveStatus('error');
    } finally {
      setIsSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-20">
        <Loader2 className="w-10 h-10 animate-spin text-primary-600" />
      </div>
    );
  }

  return (
    <div className="animate-in fade-in slide-in-from-bottom-4 duration-500 max-w-5xl mx-auto">

      <div className="mb-8">
        <h1 className="text-3xl font-extrabold text-slate-900 dark:text-white mb-2">إدارة المحتوى (CMS)</h1>
        <p className="text-slate-500 dark:text-zinc-400">
          تحكم في العبارات التسويقية والعناوين الرئيسية للموقع. أي تعديل هنا سينعكس فوراً على الموقع المباشر.
        </p>
      </div>

      <form onSubmit={handleSave} className="space-y-8">

        {/* Hero Section Module */}
        <div className="bg-white dark:bg-zinc-900 border border-slate-200 dark:border-white/10 rounded-3xl p-6 md:p-8 shadow-sm">
          <div className="flex items-center gap-3 mb-6 border-b border-slate-200 dark:border-white/10 pb-4">
            <div className="p-2 bg-primary-50 dark:bg-primary-500/10 rounded-xl text-primary-600 dark:text-primary-500">
              <Sparkles className="w-6 h-6" />
            </div>
            <h2 className="text-xl font-bold text-slate-900 dark:text-white">القسم الرئيسي (Hero Section)</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="md:col-span-2">
              <label className="block text-lg font-bold text-slate-700 dark:text-zinc-300 mb-2">النص الإرشادي (Badge)</label>
              <input
                type="text" name="heroBadge" value={formData.heroBadge || ''} onChange={handleChange} required
                className="w-full px-4 py-3 bg-slate-50 dark:bg-black/50 border border-slate-200 dark:border-white/10 rounded-xl outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 text-slate-900 dark:text-white"
              />
            </div>

            <div>
              <label className="block text-lg font-bold text-slate-700 dark:text-zinc-300 mb-2">العنوان الرئيسي الأول</label>
              <input
                type="text" name="heroTitle" value={formData.heroTitle || ''} onChange={handleChange} required
                className="w-full px-4 py-3 bg-slate-50 dark:bg-black/50 border border-slate-200 dark:border-white/10 rounded-xl outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 text-slate-900 dark:text-white"
              />
            </div>

            <div>
              <label className="block text-lg font-bold text-slate-700 dark:text-zinc-300 mb-2">العنوان الرئيسي الملون (Highlight)</label>
              <input
                type="text" name="heroHighlight" value={formData.heroHighlight || ''} onChange={handleChange} required
                className="w-full px-4 py-3 bg-slate-50 dark:bg-black/50 border border-slate-200 dark:border-white/10 rounded-xl outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 text-slate-900 dark:text-white"
              />
            </div>

            <div className="md:col-span-2">
              <label className="block text-lg font-bold text-slate-700 dark:text-zinc-300 mb-2">الوصف التسويقي</label>
              <textarea
                name="heroSubtitle" value={formData.heroSubtitle || ''} onChange={handleChange} required rows={3}
                className="w-full px-4 py-3 bg-slate-50 dark:bg-black/50 border border-slate-200 dark:border-white/10 rounded-xl outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 text-slate-900 dark:text-white resize-y"
              ></textarea>
            </div>

            <div>
              <label className="block text-lg font-bold text-slate-700 dark:text-zinc-300 mb-2">نص الزر الرئيسي</label>
              <input
                type="text" name="heroButtonText" value={formData.heroButtonText || ''} onChange={handleChange} required
                className="w-full px-4 py-3 bg-slate-50 dark:bg-black/50 border border-slate-200 dark:border-white/10 rounded-xl outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 text-slate-900 dark:text-white"
              />
            </div>

            <div>
              <label className="block text-lg font-bold text-slate-700 dark:text-zinc-300 mb-2">نص الزر الثانوي</label>
              <input
                type="text" name="heroSecondaryButtonText" value={formData.heroSecondaryButtonText || ''} onChange={handleChange} required
                className="w-full px-4 py-3 bg-slate-50 dark:bg-black/50 border border-slate-200 dark:border-white/10 rounded-xl outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 text-slate-900 dark:text-white"
              />
            </div>

            <div className="md:col-span-2">
              <label className="block text-lg font-bold text-slate-700 dark:text-zinc-300 mb-2">معرف فيديو يوتيوب (YouTube Video ID)</label>
              <div className="relative">
                <span className="absolute inset-y-0 left-0 flex items-center pl-4 text-slate-400 font-mono text-sm pointer-events-none">youtube.com/watch?v=</span>
                <input
                  type="text" name="youtubeVideoId" value={formData.youtubeVideoId || ''} onChange={handleChange} required dir="ltr"
                  className="w-full pl-44 pr-4 py-3 bg-slate-50 dark:bg-black/50 border border-slate-200 dark:border-white/10 rounded-xl outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 text-slate-900 dark:text-white font-mono"
                  placeholder="7K8A8Zxp1mg"
                />
              </div>
              <p className="mt-2 text-sm text-slate-500 dark:text-zinc-400">فقط قم بوضع المعرف الخاص بالفيديو (مثال: 7K8A8Zxp1mg).</p>
            </div>
          </div>
        </div>

        {/* Announcement Bar Module */}
        <div className="bg-white dark:bg-zinc-900 border border-slate-200 dark:border-white/10 rounded-3xl p-6 md:p-8 shadow-sm">
          <div className="flex items-center gap-3 mb-6 border-b border-slate-200 dark:border-white/10 pb-4">
            <div className="p-2 bg-yellow-50 dark:bg-yellow-500/10 rounded-xl text-yellow-600 dark:text-yellow-500">
              <MessageSquare className="w-6 h-6" />
            </div>
            <h2 className="text-xl font-bold text-slate-900 dark:text-white">شريط الإعلانات (Announcement Bar)</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">

            <div className="md:col-span-2">
              <label className="block text-lg font-bold text-slate-700 dark:text-zinc-300 mb-2">عنوان الإعلان (باللون الأصفر)</label>
              <input
                type="text" name="announcementTitle" value={formData.announcementTitle || ''} onChange={handleChange} required
                className="w-full px-4 py-3 bg-slate-50 dark:bg-black/50 border border-slate-200 dark:border-white/10 rounded-xl outline-none focus:border-yellow-500 focus:ring-2 focus:ring-yellow-500/20 text-slate-900 dark:text-white"
              />
            </div>

            <div className="md:col-span-2">
              <label className="block text-lg font-bold text-slate-700 dark:text-zinc-300 mb-2">نص الإعلان الرئيسي</label>
              <input
                type="text" name="announcementText" value={formData.announcementText || ''} onChange={handleChange} required
                className="w-full px-4 py-3 bg-slate-50 dark:bg-black/50 border border-slate-200 dark:border-white/10 rounded-xl outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 text-slate-900 dark:text-white"
              />
            </div>

            <div>
              <label className="block text-lg font-bold text-slate-700 dark:text-zinc-300 mb-2 flex items-center gap-2">
                <Tag className="w-4 h-4 text-green-500" /> سعر العرض الجديد
              </label>
              <input
                type="text" name="announcementPrice" value={formData.announcementPrice || ''} onChange={handleChange} required dir="ltr"
                className="w-full px-4 py-3 bg-slate-50 dark:bg-black/50 border border-slate-200 dark:border-white/10 rounded-xl outline-none focus:border-green-500 focus:ring-2 focus:ring-green-500/20 text-left text-slate-900 dark:text-white"
              />
            </div>

            <div>
              <label className="block text-lg font-bold text-slate-700 dark:text-zinc-300 mb-2 flex items-center gap-2">
                سعر الدورة القديم (مشطوب)
              </label>
              <input
                type="text" name="announcementOldPrice" value={formData.announcementOldPrice || ''} onChange={handleChange} required dir="ltr"
                className="w-full px-4 py-3 bg-slate-50 dark:bg-black/50 border border-slate-200 dark:border-white/10 rounded-xl outline-none focus:border-red-500 focus:ring-2 focus:ring-red-500/20 text-left text-slate-900 dark:text-white"
              />
            </div>

            <div>
              <label className="block text-lg font-bold text-slate-700 dark:text-zinc-300 mb-2 flex items-center gap-2">
                <Clock className="w-4 h-4 text-blue-500" /> مدة العرض (بالأيام)
              </label>
              <input
                type="number" name="announcementDurationDays" value={formData.announcementDurationDays || '5'} onChange={handleChange} min="1" dir="ltr"
                className="w-full px-4 py-3 bg-slate-50 dark:bg-black/50 border border-slate-200 dark:border-white/10 rounded-xl outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 text-left text-slate-900 dark:text-white"
              />
            </div>

            {/* Republish Button */}
            <div className="md:col-span-2">
              <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800/30 rounded-xl p-4">
                <div className="flex items-center justify-between gap-4 flex-wrap">
                  <div>
                    <h4 className="font-bold text-amber-800 dark:text-amber-300 text-base">إعادة نشر الإعلان</h4>
                    <p className="text-amber-600 dark:text-amber-400 text-mdmt-0.5">يعيد تشغيل العداد ويُظهر الإعلان لجميع المستخدمين حتى من أغلقوه سابقاً</p>
                  </div>
                  <button
                    type="button"
                    onClick={async () => {
                      const updated = {
                        ...formData,
                        announcementStartTime: new Date().toISOString(),
                        announcementVersion: (Number(formData.announcementVersion) || 1) + 1,
                      };
                      setFormData(updated);
                      try {
                        await updateContent(updated);
                        setSaveStatus('success');
                        setTimeout(() => setSaveStatus(null), 3000);
                      } catch (e) {
                        setSaveStatus('error');
                      }
                    }}
                    className="flex items-center gap-2 bg-amber-600 hover:bg-amber-500 text-white px-5 py-2.5 rounded-xl font-bold transition-all shadow-sm flex-shrink-0"
                  >
                    <RotateCcw className="w-4 h-4" />
                    <span>إعادة نشر الآن</span>
                  </button>
                </div>
              </div>
            </div>

          </div>
        </div>

        {/* Global Save Action */}
        <div className="flex items-center gap-4 sticky bottom-4 z-10 bg-white/80 dark:bg-zinc-900/80 backdrop-blur-md p-4 rounded-2xl border border-slate-200 dark:border-white/10 shadow-lg">
          <button
            type="submit"
            disabled={isSaving}
            className="flex items-center justify-center gap-2 bg-primary-600 hover:bg-primary-500 text-white px-8 py-3.5 rounded-xl font-bold transition-all disabled:opacity-70 shadow-[0_5px_15px_rgba(224,0,0,0.2)]"
          >
            {isSaving ? <Loader2 className="w-5 h-5 animate-spin" /> : <Save className="w-5 h-5" />}
            <span>حفظ التعديلات ونشرها</span>
          </button>

          {saveStatus === 'success' && (
            <span className="text-green-600 dark:text-green-400 font-bold animate-in fade-in duration-300">
              تم النشر بنجاح!
            </span>
          )}
          {saveStatus === 'error' && (
            <span className="text-red-600 dark:text-red-400 font-bold animate-in fade-in duration-300">
              حدث خطأ أثناء الحفظ. تأكد من اتصالك.
            </span>
          )}
        </div>

      </form>
    </div>
  );
}
